// src/components/features/chat/AssistantMark.jsx
// Ask Previu's mark — a chat-bubble glyph in a circle, used
// everywhere the assistant speaks or is named (chat header, welcome message
// label, per-message avatar).
//
// There used to be two marks for one speaker: a teal sparkle in the chat header
// and the welcome bubble, and a 🩺 stethoscope on every message the same
// assistant sent. A stethoscope also over-claims — this assistant books and
// explains, and its own footer tells the patient to verify anything clinical
// with their care team, so it should not be wearing a clinician's badge.
//
// The mark itself was originally a sparkle/star, then changed to this
// chat-bubble glyph to match the Figma reference for the assistant screen —
// same path Icon.jsx's own "chat" glyph uses, so this reads as the same mark
// as the bottom-nav Assistant tab and the Care Team page's "message" buttons,
// not a one-off shape of its own.

import React from 'react';

/** The sparkle glyph on its own — kept for HEAVY mode's decorative uses
    (WelcomeBubble's banner, ChatWindow's empty state) that predate the
    chat-bubble mark below and have no Figma reference calling for a change. */
export function SparkleIcon({ size = 20, color = 'var(--text-on-brand-dark)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
    </svg>
  );
}

/** The chat-bubble glyph on its own — same path as Icon.jsx's "chat" icon. */
function ChatBubbleIcon({ size = 20, color = 'var(--text-on-brand-dark)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke={color}
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6.5A1.5 1.5 0 0 1 5.5 5h9A1.5 1.5 0 0 1 16 6.5v5a1.5 1.5 0 0 1-1.5 1.5H8.5L5 16v-3H5.5A1.5 1.5 0 0 1 4 11.5v-5Z" />
    </svg>
  );
}

/**
 * The assistant's avatar: the chat-bubble mark in a circle.
 *
 * `solid` is the header treatment — white mark on filled teal, at the one place
 * the assistant is introduced. In the transcript the tinted version is used
 * instead: the same mark, but light enough that a column of them doesn't
 * outweigh what the assistant is actually saying.
 */
export default function AssistantMark({ size = 32, solid = false }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: solid ? 'var(--teal)' : 'var(--teal-light)',
      border: solid ? 'none' : '1.5px solid var(--teal-light)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, alignSelf: 'flex-end',
    }}>
      {/* solid=true (header/welcome label) is always on --teal, which is
          bright in both themes — --text-on-brand-dark (always white) stays
          readable rather than a raw white hex that happens to work today only
          because it was never tested elsewhere. Tinted (message avatars)
          uses --teal itself, matching the ring around it. */}
      <ChatBubbleIcon size={Math.round(size * 0.55)} color={solid ? 'var(--text-on-brand-dark)' : 'var(--teal)'} />
    </div>
  );
}
